import { Injectable, Injector, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { LstAuthService, LstUserService } from '@savvaslearning/lst-auth';

import { AuthStore } from '../stores/auth.store';
import { ClassStore } from '../stores/class.store';
import { ProjectionAppStore } from '../stores/projection-app.store';
import { ClassviewServiceClient } from '../http/classview-service-client';
import { DeviceIdentityService } from '../services/device-identity/device-identity.service';
import { HeartbeatService } from './heartbeat.service';
import {
  isCoTeacherClassRole,
  isLeadTeacherClassRole,
} from '../../shared/utils/class-role.utils';
import { ClassSessionService } from '../services/class-session/class-session.service';
import { UsageLoggingService } from '../services/usage/usage-logging.service';
import { environment } from '../../../environments/environment';
import { SessionStorageService } from '../storage/session-storage.service';

export const LOGGING_OUT_KEY = 'cv.loggingOut';

@Injectable({ providedIn: 'root' })
export class LogoutService {
  private readonly injector = inject(Injector);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly authStore = inject(AuthStore);
  private readonly classStore = inject(ClassStore);
  private readonly deviceIdentity = inject(DeviceIdentityService);
  private readonly heartbeat = inject(HeartbeatService);
  private readonly usageLogging = inject(UsageLoggingService);
  private readonly projectionApp = inject(ProjectionAppStore);

  logout(): void {
    void this.logoutAsync();
  }

  private async logoutAsync(): Promise<void> {
    const role = this.classStore.userRoleInClass();
    const classGroupId = this.classStore.classGroupId();
    // The projector never started a session, so its logout must not end the teacher's live one.
    const ownsSession = isLeadTeacherClassRole(role) || (isCoTeacherClassRole(role) && classGroupId);
    if (ownsSession && !this.projectionApp.isProjectionApp()) {
      void this.injector
        .get(ClassSessionService)
        .stopSession(classGroupId)
        .catch((err) => {
          console.warn('[logout] stopSession failed', err);
        });
    }
    this.sessionStorage.set(LOGGING_OUT_KEY, 'true');
    const usageFlush = this.usageLogging.stopAndFlush('logout');
    this.notifyBackendLogout();
    this.authStore.clearSession();
    // An explicit logout is the one legitimate reason a later login on this browser should be
    // allowed to reclaim the active-device slot (see DeviceIdentityService#clearRegistration) —
    // as opposed to a routine reload of a session that's already established, which must NOT
    // silently reclaim a slot this device may have already lost to another device.
    this.deviceIdentity.clearRegistration();
    this.classStore.clearSelection();
    if (this.heartbeat.hasTimer) {
      this.heartbeat.destroy();
      await Promise.allSettled([
        usageFlush,
        firstValueFrom(this.heartbeat.sendEvent('logoutEvent')),
      ]);
      this.redirectToLogout();
    } else {
      await usageFlush;
      this.redirectToLogout();
    }
  }

  private notifyBackendLogout(): void {
    if (environment.useLegacyBackend) {
      return;
    }
    // No RBS token means there's no authenticated session to notify.
    if (!this.injector.get(LstUserService, null)?.getRbsToken()) {
      return;
    }
    void this.injector
      .get(ClassviewServiceClient)
      .post('/v1/logout', {})
      .catch((err: unknown) => console.warn('[logout] backend logout notify failed', err));
  }

  private redirectToLogout(): void {
    this.projectionApp.leave();
    const lstAuth = this.injector.get(LstAuthService, null);
    lstAuth?.redirectToSSOLogoutPage().pipe(take(1)).subscribe();
  }
}
